/**
 * Design token values — JS-accessible mirror of the CSS custom properties
 * defined in `src/styles/global.css`.
 *
 * ## Why this exists
 *
 * CSS variables (`var(--token)`) work perfectly inside `CSSProperties` objects
 * for direct property values.  However they **cannot** be used in contexts
 * that require raw colour values:
 *
 *   - Canvas 2D API (`ctx.fillStyle`, `ctx.strokeStyle`)
 *   - Computed `rgba()` strings (`rgba(R, G, B, alpha)`)
 *   - SVG `stopColor` attributes (JSX props, not CSS)
 *   - Dynamic `boxShadow` strings that embed a colour
 *
 * Rather than scattering raw hex literals across feature files, every JS
 * context that needs an actual colour value should import from this module.
 *
 * ## Rules
 *
 * 1. **Never add a colour here that is not also defined in global.css.**
 *    This module mirrors the CSS `:root` block — it does not extend it.
 * 2. When adding a new token, add the CSS variable **first**, then mirror it here.
 * 3. Components that can use `var(--token)` directly (i.e. in `CSSProperties`)
 *    should still prefer the CSS variable form.  This module is only for
 *    canvas, computed strings, and SVG attributes.
 */

// ── Backgrounds ──────────────────────────────────────────────────────
export const bgPrimary = '#09090b'
export const bgSecondary = '#0f0f12'
export const bgTertiary = '#18181b'
export const bgElevated = '#1f1f23'

// ── Text ─────────────────────────────────────────────────────────────
export const textPrimary = '#fafafa'
export const textSecondary = '#a1a1aa'
export const textMuted = '#71717a'
export const textFaint = '#52525b'

// ── Accent ───────────────────────────────────────────────────────────
export const accentPrimary = '#3B82F6'
export const accentSecondary = '#60A5FA'
export const accentDeep = '#2563EB'

// ── Semantic ─────────────────────────────────────────────────────────
export const success = '#22c55e'
export const warning = '#f59e0b'
export const error = '#ef4444'
export const info = '#3b82f6'

// ── Category ─────────────────────────────────────────────────────────
export const categoryActivity = '#3b82f6'
export const categoryTelemetry = '#6b7280'
export const categoryLifecycle = '#22c55e'
export const categoryOperational = '#f59e0b'
export const categoryCommunication = '#8b5cf6'

// ── Domain ───────────────────────────────────────────────────────────
export const domainSupport = '#3b82f6'
export const domainEngineering = '#8b5cf6'
export const domainMarketing = '#f59e0b'
export const domainOperations = '#10b981'
export const domainGrowth = '#ec4899'
export const domainPlatform = '#6b7280'

// ── Extended palette ─────────────────────────────────────────────────
// Colours used in specific feature contexts (each also defined in :root).
export const purple = '#a855f7'
export const slate = '#94a3b8'
export const orange = '#f97316'
export const foam = '#93c5fd'

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Build an `rgba()` string from a hex colour and alpha value.
 *
 * Accepts 3-char (#rgb), 6-char (#rrggbb) and 8-char (#rrggbbaa) hex.
 *
 * ```ts
 * rgba(accentPrimary, 0.15) // => 'rgba(59, 130, 246, 0.15)'
 * ```
 */
export function rgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const full = h.length === 3
    ? h.split('').map((c) => c + c).join('')
    : h.slice(0, 6)
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
